import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import BookingHook from '../../hooks/BookingHook';
import AgencyStatsHook from '../../hooks/AgencyStatsHook';
import { DatePicker,ConfigProvider  } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';


import moment from "moment";
import kabba_icon from '../../assets/icons/kabba1.png'
import madina_icon from '../../assets/icons/madina1.png'

// Define all possible date formats your bookings might use
const DATE_FORMATS = ["YYYY-MM-DD", "DD-MM-YYYY", "MM-DD-YYYY","DD/MM/YYYY"];

// Helper function to parse and format dates
const parseAndFormatDate = (dateString) => {
  if (!dateString) return null;
  // Try parsing with multiple formats. If successful, format to YYYY-MM-DD
  const m = moment(dateString, DATE_FORMATS, true); // `true` for strict parsing
  return m.isValid() ? m.format("YYYY-MM-DD") : null;
};


export default function Dashboard() {
  const { getBookings } = BookingHook();
  const { getAgencyStats,totalSearches,totalClicks,profileClicks } = AgencyStatsHook();

  useEffect(() => {
    getAgencyStats()
    getBookings()
    document.title = "Dashboard"
  }, [])

  const collapsed = useSelector((state) => state.collapsed.collapsed);
  const bookings = useSelector((state) => state.bookings.bookings);
  const todayDate = moment().format("YYYY-MM-DD");

  // Pre-process bookings to ensure all relevant dates are in YYYY-MM-DD format
  const processedBookings = bookings.map(booking => {
    // Create a shallow copy of the top-level booking object
    const newBooking = { ...booking };
  
    // --- Process arrival_flight details with deep copying ---
    if (newBooking.arrival_flight) {
      // Create a NEW object for arrival_flight to avoid modifying the original
      newBooking.arrival_flight = { ...newBooking.arrival_flight };
  
      // Process arrival_flight_date
      if (newBooking.arrival_flight.arrival_flight_date) {
        // console.log('newBooking.arrival_flight?.arrival_flight_date (before parse):', newBooking.arrival_flight.arrival_flight_date);
        newBooking.arrival_flight.arrival_flight_date = parseAndFormatDate(newBooking.arrival_flight.arrival_flight_date);
        // console.log('newBooking.arrival_flight?.arrival_flight_date (after parse):', newBooking.arrival_flight.arrival_flight_date);
      }
      // Process take_off_flight_date (if it exists)
      if (newBooking.arrival_flight.take_off_flight_date) {
        newBooking.arrival_flight.take_off_flight_date = parseAndFormatDate(newBooking.arrival_flight.take_off_flight_date);
      }
    }
  
    // --- Process departure_flight details with deep copying ---
    if (newBooking.departure_flight) {
      // Create a NEW object for departure_flight to avoid modifying the original
      newBooking.departure_flight = { ...newBooking.departure_flight };
  
      // Process departure_flight_date
      if (newBooking.departure_flight.departure_flight_date) {
        newBooking.departure_flight.departure_flight_date = parseAndFormatDate(newBooking.departure_flight.departure_flight_date);
      }
      // Process departure_to_flight_date (if it exists)
      if (newBooking.departure_flight.departure_to_flight_date) {
        newBooking.departure_flight.departure_to_flight_date = parseAndFormatDate(newBooking.departure_flight.departure_to_flight_date);
      }
    }
  
    // --- Process accommodation_details with deep copying for each detail object ---
    if (newBooking.accomodation_details && Array.isArray(newBooking.accomodation_details)) {
      newBooking.accomodation_details = newBooking.accomodation_details.map(detail => ({
        ...detail, // Create a shallow copy of each individual detail object
        check_in_date: parseAndFormatDate(detail.check_in_date),
        check_out_date: parseAndFormatDate(detail.check_out_date),
      }));
    }
  
    return newBooking;
  }).filter(booking => {
    // Optional: Filter out bookings that failed to parse critical dates if necessary
    // For example, if arrival_flight_date is crucial:
    return booking.arrival_flight?.arrival_flight_date !== null;
  })
  
  // Filter current bookings
  const currentBookings = processedBookings.filter((booking) =>
    moment(booking.arrival_flight?.arrival_flight_date).isAfter(todayDate)
  );
  const totalBookings = processedBookings.reduce((total, data) => total + (data.totalMembers || 0), 0);

  const upcomingBookings = processedBookings.filter((booking) =>
    moment(booking.departure_flight?.departure_flight_date).isAfter(todayDate)
  );
  const totalUpcomingBookings = upcomingBookings.reduce((total, data) => total + (data.totalMembers || 0), 0);


  const pastBookings = processedBookings.filter((booking) =>
    moment(booking.arrival_flight?.arrival_flight_date).isBefore(todayDate)
  );
  const totalPastBookings = pastBookings.reduce((total, data) => total + (data.totalMembers || 0), 0);

  // filtering by date
  const [date, setDate] = useState(todayDate);

  const handleDateChange = (dateObject, dateString) => {
    if (dateObject) {
      // Convert the dateObject (dayjs object) or dateString to your desired YYYY-MM-DD format
      // dateObject.format("YYYY-MM-DD") is the most reliable way to get YYYY-MM-DD from dayjs
      setDate(dateObject.format("YYYY-MM-DD"));
    } else {
      setDate(null); // Handle clearing the date
    }
  };

  useEffect(() => {
    // This effect will run when `date` changes.
    // You might want to re-calculate some derived states here if they don't depend directly on `date` in their definition.
  }, [date]);

  // Pre-filter bookings for return_date
  // Now using `processedBookings` which have normalized dates
  const filteredBookings = processedBookings.filter((booking) =>
    moment(booking.arrival_flight?.arrival_flight_date).isAfter(date ? date : todayDate)
  );

  // Function to filter bookings based on city
  const filterBookingsByCity = (city, bookingsToFilter, checkDate) => {
    if (!city || !checkDate) {
      return [];
    }

    const normalizedCity = city.trim().toLowerCase();
    // checkDate is already expected to be in YYYY-MM-DD format due to `parseAndFormatDate` or `todayDate`
    const formattedCheckDate = checkDate;

    return bookingsToFilter.filter((booking) => {
      // Check if it's a hotel booking
      if (booking.isHotel) {
        const accomodationDetails = booking.accomodation_details || [];

        // Check if any of the accommodation details match the criteria
        return accomodationDetails.some((detail) => {
          const { hotel_city, check_in_date, check_out_date } = detail || {};
          return (
            hotel_city?.trim().toLowerCase().includes(normalizedCity) &&
            moment(check_in_date).isSameOrBefore(formattedCheckDate) &&
            moment(check_out_date).isAfter(formattedCheckDate)
          );
        });
      }

      // Check for 'umrah' package type for non-hotel bookings
      if (!booking.package_header?.trim().toLowerCase().includes("umrah")) {
        return false;
      }

      const accomodationDetails = booking.accomodation_details || [];

      // Helper function to check city and date range
      const isCityAndDateMatch = (hotelCity, checkInDate, checkOutDate) => {
        if (!hotelCity || !checkInDate || !checkOutDate) {
          return false;
        }
        return (
          hotelCity.trim().toLowerCase().includes(normalizedCity) &&
          moment(checkInDate).isSameOrBefore(formattedCheckDate) &&
          moment(checkOutDate).isAfter(formattedCheckDate)
        );
      };

      // Check for matches in non-hotel accommodation details
      return accomodationDetails.some((detail) => {
        return isCityAndDateMatch(
          detail?.hotel_city,
          detail?.check_in_date,
          detail?.check_out_date
        );
      });
    });
  };

  // Calculate total pilgrims in a given city
  const calculateTotalPilgrims = (city) => {
    const checkDate = date || todayDate; // Use `todayDate` as fallback
    const cityFilteredBookings = filterBookingsByCity(city, processedBookings, checkDate); // Use processedBookings here
    return cityFilteredBookings.reduce((sum, booking) => sum + (booking.totalMembers || 0), 0);
  };


  // Calculate totals
  const totalPilgrimsInMakkah = calculateTotalPilgrims("makkah");
  const totalPilgrimsInMadina = calculateTotalPilgrims("madinah");

  // Going
  const goingPilgrims = processedBookings
    .filter((booking) =>
      moment(booking.departure_flight?.departure_flight_date).isSame(date ? date : todayDate, 'day')
    )
    .reduce((sum, booking) => sum + (booking.totalMembers || 0), 0);
  // Coming
  const comingPilgrims = processedBookings.filter((booking) =>
    moment(booking.arrival_flight?.arrival_flight_date).isSame(date ? date : todayDate)
  ).reduce((sum, booking) => sum + (booking.totalMembers || 0), 0);


  // Calculate Gender Statistics for Room Type
  const [city, setCity] = useState('');

  const calculateGenderStats = (bookingsToFilter, checkDate, cityToFilter) => {
    const normalizedCity = cityToFilter.trim().toLowerCase();
    const formattedCheckDate = checkDate; // Already formatted

    // Helper function to check city and date match
    const isCityAndDateMatch = (hotelCity, checkInDate, checkOutDate) => {
      if (!hotelCity || !checkInDate || !checkOutDate) return false;
      return (
        hotelCity.trim().toLowerCase().includes(normalizedCity) &&
        moment(checkInDate).isSameOrBefore(formattedCheckDate) &&
        moment(checkOutDate).isSameOrAfter(formattedCheckDate)
      );
    };

    // Filter bookings based on city and date
    const filteredBookingsForGender = bookingsToFilter.filter((booking) => {
      if (booking.isHotel) {
        // For hotel bookings, check accomodation_details array
        return (booking.accomodation_details || []).some((detail) =>
          isCityAndDateMatch(detail?.hotel_city, detail?.check_in_date, detail?.check_out_date)
        );
      } else {
        // For non-hotel bookings, check 'umrah' package and cities
        if (!booking.package_header?.trim().toLowerCase().includes("umrah")) return false;

        const accomodationDetails = booking.accomodation_details || [];
        return accomodationDetails.some((detail) =>
          isCityAndDateMatch(detail?.hotel_city, detail?.check_in_date, detail?.check_out_date)
        );
      }
    });

    // Calculate gender statistics for each room type
    const roomTypeStats = [];
    let totalMale = 0;
    let totalFemale = 0;

    filteredBookingsForGender.forEach((booking) => {
      const { room_type, members } = booking;

      // Find or create a stats entry for the current room type
      let roomTypeStat = roomTypeStats.find((stat) => stat.roomType === room_type);
      if (!roomTypeStat) {
        roomTypeStat = { roomType: room_type, male: 0, female: 0 };
        roomTypeStats.push(roomTypeStat);
      }

      // Count male and female members
      (members || []).forEach((member) => {
        if (member.gender?.toLowerCase() === "male") {
          roomTypeStat.male += 1;
          totalMale += 1;
        } else if (member.gender?.toLowerCase() === "female") {
          roomTypeStat.female += 1;
          totalFemale += 1;
        }
      });
    });

    // Return the gender statistics
    return {
      roomTypeStats,
      totalMale,
      totalFemale,
    };
  };


  const checkDate = date || todayDate; // Assuming `date` and `todayDate` are defined

  // Call the function and store the result in genderStats
  const genderStats = calculateGenderStats(processedBookings, checkDate, city); // Use processedBookings
  // COLORS for pie chart
  const COLORS = ['#00ABFB', '#EF5FCB'];
  const totalPilgrims = totalPilgrimsInMakkah + totalPilgrimsInMadina + goingPilgrims + comingPilgrims;


  const cityStats = Object.values(
    processedBookings.reduce((acc, booking) => { // Use processedBookings
      const {
        departure_flight = {},
        arrival_flight = {},
        package_header = '',
        totalMembers = 0,
      } = booking;


      const isUmrahPackage = package_header.trim().toLowerCase().includes('umrah');

      const selectedDate = date || todayDate;


      // Normalize city values for check-in and check-out
      const checkInCity = (moment(departure_flight.departure_to_flight_date).isSame(selectedDate, "day") ? departure_flight.departure_to_location?.trim().toLowerCase() :
        moment(arrival_flight.take_off_flight_date).isSame(selectedDate, "day") ?
          arrival_flight.take_off_location?.trim().toLowerCase() : '');

      const checkOutCity = (moment(departure_flight.departure_flight_date).isSame(selectedDate, "day") ? departure_flight.departure_location?.trim().toLowerCase() :
        moment(arrival_flight.arrival_flight_date).isSame(selectedDate, "day") ?
          arrival_flight.arrival_location?.trim().toLowerCase() : '');


      // Process check-in stats
      if (
        isUmrahPackage &&
        (moment(departure_flight.departure_to_flight_date).isSame(selectedDate, "day") ||
          moment(arrival_flight.take_off_flight_date).isSame(selectedDate, "day"))
      ) {

        if (checkInCity) {
          if (!acc[checkInCity]) {

            acc[checkInCity] = { city: checkInCity, checkin: 0, checkout: 0 };
          }
          acc[checkInCity].checkin += totalMembers;

        }
      }

      // Process check-out stats
      if (
        isUmrahPackage &&
        (moment(departure_flight.departure_flight_date).isSame(selectedDate, "day") ||
          moment(arrival_flight.arrival_flight_date).isSame(selectedDate, "day"))
      ) {

        if (checkOutCity) {
          if (!acc[checkOutCity]) {

            acc[checkOutCity] = { city: checkOutCity, checkin: 0, checkout: 0 };
          }
          acc[checkOutCity].checkout += totalMembers;

        }
      }

      return acc;
    }, {})
  );


  const filterTotalCheckIn = (cityToFilter, bookingsToFilter, checkDate) => {
    const normalizedCity = cityToFilter.trim().toLowerCase();

    return bookingsToFilter.filter((booking) => {
      // For hotel bookings with isHotel === true
      if (booking.isHotel) {
        return (booking.accomodation_details || []).some((detail) => {
          return (
            detail.hotel_city?.trim().toLowerCase().includes(normalizedCity) &&
            moment(detail.check_in_date).isSame(checkDate)
          );
        });
      }

      // For non-hotel bookings with 'umrah' package type
      if (!booking.package_header?.trim().toLowerCase().includes("umrah")) {
        return false;
      }

      const accomodationDetails = booking.accomodation_details || [];
      return accomodationDetails.some((detail) => {
        return (
          detail.hotel_city?.trim().toLowerCase().includes(normalizedCity) &&
          moment(detail.check_in_date).isSame(checkDate)
        );
      });
    });
  };


  // Calculate total pilgrims in a given city
  const calculateTotalCheckIn = (city) => {
    const checkDate = date || todayDate;

    const cityFilteredBookings = filterTotalCheckIn(city, processedBookings, checkDate); // Use processedBookings

    return cityFilteredBookings.reduce((sum, booking) => sum + (booking.totalMembers || 0), 0);
  };


  const filterTotalCheckOut = (cityToFilter, bookingsToFilter, checkDate) => {
    const normalizedCity = cityToFilter.trim().toLowerCase();

    return bookingsToFilter.filter((booking) => {
      // For hotel bookings with isHotel === true
      if (booking.isHotel) {
        return (booking.accomodation_details || []).some((detail) => {
          return (
            detail.hotel_city?.trim().toLowerCase().includes(normalizedCity) &&
            moment(detail.check_out_date).isSame(checkDate)
          );
        });
      }

      // For non-hotel bookings with 'umrah' package type
      if (!booking.package_header?.trim().toLowerCase().includes("umrah")) {
        return false;
      }

      const accomodationDetails = booking.accomodation_details || [];
      return accomodationDetails.some((detail) => {
        return (
          detail.hotel_city?.trim().toLowerCase().includes(normalizedCity) &&
          moment(detail.check_out_date).isSame(checkDate)
        );
      });
    });
  };
  
  // Calculate total pilgrims in a given city
  const calculateTotalCheckOut = (city) => {
    const checkDate = date ? date : todayDate;
    const cityFilteredBookings = filterTotalCheckOut(city, filteredBookings, checkDate);
    return cityFilteredBookings.reduce((sum, booking) => sum + (booking.totalMembers || 0), 0);
  };
  return (
    <div className={`${collapsed ? "collapsed mt-5 " : "main mt-5"}`}>
      <div className="container-fluid bg-none my-0 mt-4 dashboard">
        <div className="row px-2 mt-5 pt-4">

        <div className="col-md-12 data_table p-2 pb-3">
            <div className="d-flex justify-content-between searchbar pb-2 pt-1 px-md-2 my-auto">
              <div className="d-flex left my-auto ps-3 ps-md-0">
                <h4 className='my-auto'>Analytics</h4>
              </div>
             
            </div>

            <div className="row px-3 mt-2">
              <div className="col-md-4 my-1 px-1">
                <div className="content ">
                  <div className="d-flex justify-content-between  px-2 pt-3 pb-4">
                    <div className="left my-auto">
                      <h6 className='my-auto'>Package Searches</h6>

                    </div>
                    <div className="right my-auto">
                      <h5 className='my-auto'>{totalSearches}</h5>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4 my-1 px-1">
                <div className="content">
                  <div className="d-flex justify-content-between px-2 pt-3 pb-4">
                    <div className="left my-auto">
                      <h6 className=' my-auto'>Package Clicks</h6>
                     

                    </div>
                    <div className="right mty-auto">
                      <h5 className='my-auto'>{totalClicks}</h5>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4 my-1 px-1">
                <div className="content ">
                  <div className="d-flex justify-content-between px-2 pt-3 pb-4">
                    <div className="left my-auto">
                      <h6 className='my-auto'>Conatct Us Clicks</h6>
                      

                    </div>
                    <div className="right my-auto">
                      <h5 className='my-auto'>{profileClicks}</h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-12 data_table p-2 pb-3 mt-2">
            <div className="d-flex justify-content-between searchbar pb-2 pt-1 px-md-2 my-auto">
              <div className="d-flex left my-auto ps-3 ps-md-0">
                <h4 className='my-auto'>Bookings</h4>
              </div>
              {/* <div className="d-flex right my-auto">
                <label htmlFor="" className='my-auto'>Filter by</label>
                <select name="" id="" className='ms-2 my-auto' value={search} onChange={(e)=>setSearch(e.target.value)} >
                  <option value="">All</option>
                  <option value="Today">Today</option>
                  <option value="This Week">This Week</option>
                  
                </select>
              </div> */}
            </div>

            <div className="row px-3 mt-2">
              <div className="col-md-4 my-1 px-1">
                <div className="content ">
                  <div className="d-flex justify-content-between  px-2 pt-3 pb-4">
                    <div className="left my-auto">
                      <h6 className='my-auto'>All Bookings</h6>
                      {/* <span className='up'>+ 34 <svg width="10" height="11" viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2.0835 4.66667L5.00016 1.75M5.00016 1.75L7.91683 4.66667M5.00016 1.75V9.25" stroke="#22C55E" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                      </span> */}

                    </div>
                    <div className="right my-auto">
                      <h5 className='my-auto'>{totalBookings && totalBookings}</h5>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4 my-1 px-1">
                <div className="content">
                  <div className="d-flex justify-content-between px-2 pt-3 pb-4">
                    <div className="left my-auto">
                      <h6 className=' my-auto'>Upcoming Bookings</h6>
                      {/* <span className='down'>- 34 <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.25033 6.33333L5.33366 9.25M5.33366 9.25L2.41699 6.33333M5.33366 9.25L5.33366 1.75" stroke="#EF4444" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>

                      </span> */}

                    </div>
                    <div className="right mty-auto">
                      <h5 className='my-auto'>{totalUpcomingBookings && totalUpcomingBookings}</h5>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4 my-1 px-1">
                <div className="content ">
                  <div className="d-flex justify-content-between px-2 pt-3 pb-4">
                    <div className="left my-auto">
                      <h6 className='my-auto'>Past Bookings</h6>
                      {/* <span className='up'>+ 34 <svg width="10" height="11" viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2.0835 4.66667L5.00016 1.75M5.00016 1.75L7.91683 4.66667M5.00016 1.75V9.25" stroke="#22C55E" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                      </span> */}

                    </div>
                    <div className="right my-auto">
                      <h5 className='my-auto'>{totalPastBookings && totalPastBookings}</h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Pilgrims Details */}

          <div className="col-md-12 data_table p-2 pb-3 mt-2">
            <div className="d-flex justify-content-between searchbar pb-2 pt-1 px-md-2 my-auto">
              <div className="d-flex left my-auto ps-3 ps-md-0">
                <h4 className='my-auto'>Pilgrims Details</h4>
              </div>
              <div className="d-flex right my-auto">
                {/* <label htmlFor="" className='my-auto'>Filter by</label> */}
                <DatePicker
        // For value, always parse your state 'date' explicitly to dayjs with its known format
        // This ensures dayjs correctly understands the 'YYYY-MM-DD' string from your state.
        value={date ? dayjs(date, "YYYY-MM-DD") : null}
        placeholder="Select Date"
        onChange={handleDateChange} // Use the new handler
        format="DD/MM/YYYY" // Display format for the user
      />
                {/* <input type="date" value={date} onChange={(e) => setDate(e.target.value)} /> */}
              </div>
            </div>
            <hr />

            <div className="row px-4 mt-2">
              <div className="col-lg-8 ">
                <div className="row content px-2 pt-2 pb-4">
                  <h5>Total Pilgrims: <span>{totalPilgrims}</span></h5>
                  <div className="col-md-3 my-1 px-1">
                    <div className="px-2 py-3 content pilgrims makkah_bg">
                      <div className="d-flex justify-content-between px-2">
                        <div className="left">
                          <h6>In Makkah</h6>
                          {/* <span className='up'>+ 34 <svg width="10" height="11" viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2.0835 4.66667L5.00016 1.75M5.00016 1.75L7.91683 4.66667M5.00016 1.75V9.25" stroke="#22C55E" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                          </span> */}
                          <div className="icon">
                            <img src={kabba_icon} alt="" />
                          </div>
                        </div>
                        <div className="right mt-auto">
                          <h5 className='mt-auto'>{totalPilgrimsInMakkah}</h5>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-3 my-1 px-1">
                    <div className="content px-2 py-3 pilgrims madina_bg ">
                      <div className="d-flex justify-content-between px-2">
                        <div className="left">
                          <h6>In Madinah</h6>
                          {/* <span className='down'>- 34 <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8.25033 6.33333L5.33366 9.25M5.33366 9.25L2.41699 6.33333M5.33366 9.25L5.33366 1.75" stroke="#EF4444" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                          </span> */}
                          <div className="icon">
                            <img src={madina_icon} alt="" />
                          </div>
                        </div>
                        <div className="right mt-auto">
                          <h5 className='mt-auto'>{totalPilgrimsInMadina}</h5>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3 my-1 px-1">
                    <div className="content pilgrims going_bg px-2 py-3">
                      <div className="d-flex justify-content-between px-2">
                        <div className="left">
                          <h6>Going</h6>
                          {/* <span className='up'>+ 34 <svg width="10" height="11" viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2.0835 4.66667L5.00016 1.75M5.00016 1.75L7.91683 4.66667M5.00016 1.75V9.25" stroke="#22C55E" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                          </span> */}
                          <i className="fa-solid fa-plane-departure text-white"></i>

                        </div>
                        <div className="right mt-auto">
                          <h5 className='mt-auto'>{goingPilgrims && goingPilgrims}</h5>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-3 my-1 px-1">
                    <div className="content pilgrims coming_bg px-2 py-3">
                      <div className="d-flex justify-content-between px-2">
                        <div className="left">
                          <h6>Coming back </h6>
                          {/* <span className='up'>+ 34 <svg width="10" height="11" viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2.0835 4.66667L5.00016 1.75M5.00016 1.75L7.91683 4.66667M5.00016 1.75V9.25" stroke="#22C55E" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                          </span> */}
                          <i className="fa-solid fa-plane-arrival text-white"></i>
                        </div>
                        <div className="right mt-auto">
                          <h5 className='mt-auto'>{comingPilgrims && comingPilgrims}</h5>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-4 col-12">
                <TableContainer className='dashboard_table content px-2 pt-3 pb-4'>
                  <Table stickyHeader aria-label="simple table" className='text-center'>
                    <TableHead>
                      <TableRow>
                        <TableCell className='th text-center py-2' >Hotel City</TableCell>
                        <TableCell className='th text-center py-2' >Check In</TableCell>
                        <TableCell className='th text-center py-2' >Check Out</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>

                      <TableRow>
                        <TableCell className='td text-center border-0' >
                          Madinah
                        </TableCell>
                        <TableCell className='td text-center border-0'>
                          <span className='down'><svg width="13" height="13" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8.25033 6.33333L5.33366 9.25M5.33366 9.25L2.41699 6.33333M5.33366 9.25L5.33366 1.75" stroke="#EF4444" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>

                          </span>
                          {calculateTotalCheckIn('madinah')}
                        </TableCell>
                        <TableCell className='td text-center border-0' >
                          <span className='up'><svg width="13" height="13" viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2.0835 4.66667L5.00016 1.75M5.00016 1.75L7.91683 4.66667M5.00016 1.75V9.25" stroke="#22C55E" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                          </span>
                          {calculateTotalCheckOut('madinah')}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell className='td text-center border-0' >
                          Makkah
                        </TableCell>
                        <TableCell className='td text-center border-0' >
                          <span className='down'><svg width="13" height="13" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8.25033 6.33333L5.33366 9.25M5.33366 9.25L2.41699 6.33333M5.33366 9.25L5.33366 1.75" stroke="#EF4444" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>

                          </span>
                          {calculateTotalCheckIn('makkah')}

                        </TableCell>
                        <TableCell className='td text-center border-0' >
                          <span className='up'><svg width="13" height="13" viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2.0835 4.66667L5.00016 1.75M5.00016 1.75L7.91683 4.66667M5.00016 1.75V9.25" stroke="#22C55E" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                          </span>
                          {calculateTotalCheckOut('makkah')}
                        </TableCell>
                      </TableRow>

                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </div>
            {/* <hr className='my-2' />
            <div className="row px-2">
              <div className="col-md-12">
                <ul className="nav nav-pills mb-3 d-flex justify-content-between" id="pills-tab" role="tablist">
                  <li className="nav-item flex-grow-1" role="presentation">
                    <button
                      className="nav-link active w-100 text-center left_btn"
                      id="pills-checkin-tab"
                      data-bs-toggle="pill"
                      data-bs-target="#pills-checkin"
                      type="button"
                      role="tab"
                      aria-controls="pills-checkin"
                      aria-selected="true"
                    >
                      Check in Details
                    </button>
                  </li>
                  <li className="nav-item flex-grow-1" role="presentation">
                    <button
                      className="nav-link w-100 text-center right_btn"
                      id="pills-checkout-tab"
                      data-bs-toggle="pill"
                      data-bs-target="#pills-checkout"
                      type="button"
                      role="tab"
                      aria-controls="pills-checkout"
                      aria-selected="false"
                    >
                      Check out Details
                    </button>
                  </li>
                </ul>
                <div className="tab-content" id="pills-tabContent">
                  <div className="tab-pane fade show active" id="pills-checkin" role="tabpanel" aria-labelledby="pills-checkin-tab">
                    <div className="row px-2 mt-2">
                      <h5>Arrival To</h5>
                      <div className="col-md-4 my-1 px-1">
                        <div className="p-2 content pilgrims jeddah_bg">
                          <div className="d-flex justify-content-between px-2">
                            <div className="left">
                              <h6>Jeddah</h6>
                              <span className='up'>+ 34 <svg width="10" height="11" viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.0835 4.66667L5.00016 1.75M5.00016 1.75L7.91683 4.66667M5.00016 1.75V9.25" stroke="#22C55E" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                              </svg>
                              </span>
                            </div>
                            <div className="right mt-auto">
                              <h5 className='mt-auto'>
                                {bookings && bookings
                                  .filter(booking =>
                                    booking.package_header?.trim().toLowerCase().includes('umrah') &&
                                    booking.arrival_flight.arrival_location.trim().toLowerCase().includes('jeddah') &&
                                    moment(booking.arrival_flight.arrival_flight_date, "YYYY-MM-DD").isSame(date ? date : todayDate, 'day')
                                  )
                                  .reduce((sum, booking) => sum + (booking.totalMembers || 0), 0)}
                              </h5>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-4 my-1 px-1">
                        <div className="content pilgrims madina_bg  p-2">
                          <div className="d-flex justify-content-between px-2">
                            <div className="left">
                              <h6>Madina</h6>
                              <span className='down'>- 34 <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8.25033 6.33333L5.33366 9.25M5.33366 9.25L2.41699 6.33333M5.33366 9.25L5.33366 1.75" stroke="#EF4444" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                              </svg>

                              </span>
                            </div>
                            <div className="right mt-auto">
                              <h5 className='mt-auto'>
                                {bookings && bookings
                                  .filter(booking =>
                                    booking.package_header.trim().toLowerCase().includes('umrah') &&
                                    booking.arrival_flight.arrival_location.trim().toLowerCase().includes('madina') &&
                                    moment(booking.arrival_flight.arrival_flight_date, "YYYY-MM-DD").isSame(date ? date : todayDate, 'day')
                                  )
                                  .reduce((sum, booking) => sum + (booking.totalMembers || 0), 0)}
                              </h5>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4 my-1 px-1">
                        <div className="content pilgrims makkah_bg p-2">
                          <div className="d-flex justify-content-between px-2">
                            <div className="left">
                              <h6>Makkah</h6>
                              <span className='up'>+ 34 <svg width="10" height="11" viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.0835 4.66667L5.00016 1.75M5.00016 1.75L7.91683 4.66667M5.00016 1.75V9.25" stroke="#22C55E" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                              </svg>
                              </span>

                            </div>
                            <div className="right mt-auto">
                              <h5 className='mt-auto'>
                                {bookings && bookings
                                  .filter(booking =>
                                    booking.package_header.trim().toLowerCase().includes('umrah') &&
                                    booking.arrival_flight.arrival_location.trim().toLowerCase().includes('makkah') &&
                                    moment(booking.arrival_flight.arrival_flight_date, "YYYY-MM-DD").isSame(date ? date : todayDate, 'day')
                                  )
                                  .reduce((sum, booking) => sum + (booking.totalMembers || 0), 0)}
                              </h5>
                            </div>
                          </div>
                        </div>
                      </div>

                      {bookings && Object.values(bookings
                        .filter(booking =>
                          // Filter out 'madina', 'makkah', 'jeddah' from arrival_location
                          !['madina', 'makkah', 'jeddah'].some(location => booking.arrival_flight.arrival_location.toLowerCase().includes(location)) &&
                          booking.package_header.trim().toLowerCase().includes('umrah') &&
                          // Check if the arrival flight date matches the selected date (date or todayDate)
                          moment(booking.arrival_flight.arrival_flight_date, "YYYY-MM-DD").isSame(date ? date : todayDate, 'day')
                        )
                        .reduce((acc, booking) => {
                          const location = booking.arrival_flight.arrival_location;

                          // If this location has not been added to the accumulator, add it
                          if (!acc[location]) {
                            acc[location] = {
                              location,
                              totalMembers: 0
                            };
                          }

                          // Sum the totalMembers for each location
                          acc[location].totalMembers += booking.totalMembers || 0;

                          return acc;
                        }, {})) // Convert the accumulator object to an array of location stats
                        .filter(locationStat => locationStat.totalMembers > 0) // Only include locations with totalMembers > 0
                        .map(locationStat => (
                          <div key={locationStat.location} className="col-md-4 my-1 px-1">
                            <div className="p-2 content pilgrims airport_bg">
                              <div className="d-flex justify-content-between px-2">
                                <div className="left">
                                  <h6>{locationStat.location}</h6>
                                  <span className='up'>
                                    + {locationStat.totalMembers}{' '}
                                    <svg width="10" height="11" viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M2.0835 4.66667L5.00016 1.75M5.00016 1.75L7.91683 4.66667M5.00016 1.75V9.25" stroke="#22C55E" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </span>
                                </div>
                                <div className="right mt-auto">
                                  <h5 className='mt-auto'>{locationStat.totalMembers}</h5>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}


                    </div>
                  </div>

                  <div className="tab-pane fade" id="pills-checkout" role="tabpanel" aria-labelledby="pills-checkout-tab">
                    <div className="row px-2 mt-2">
                      <h5>Departure From</h5>

                      <div className="col-md-4 my-1 px-1">
                        <div className="p-2 content pilgrims jeddah_bg">
                          <div className="d-flex justify-content-between px-2">
                            <div className="left">
                              <h6>Jeddah</h6>
                              <span className='up'>+ 34 <svg width="10" height="11" viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.0835 4.66667L5.00016 1.75M5.00016 1.75L7.91683 4.66667M5.00016 1.75V9.25" stroke="#22C55E" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                              </svg>
                              </span>
                            </div>
                            <div className="right mt-auto">
                              <h5 className='mt-auto'>
                                {bookings && bookings
                                  .filter(booking =>
                                    booking.package_header.trim().toLowerCase().includes('umrah') &&
                                    booking.departure_flight.departure_location.trim().toLowerCase().includes('jeddah') &&
                                    moment(booking.departure_flight.departure_flight_date, "YYYY-MM-DD").isSame(date ? date : todayDate, 'day')
                                  )
                                  .reduce((sum, booking) => sum + (booking.totalMembers || 0), 0)}
                              </h5>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-4 my-1 px-1">
                        <div className="content pilgrims madina_bg  p-2">
                          <div className="d-flex justify-content-between px-2">
                            <div className="left">
                              <h6>Madina</h6>
                              <span className='down'>- 34 <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8.25033 6.33333L5.33366 9.25M5.33366 9.25L2.41699 6.33333M5.33366 9.25L5.33366 1.75" stroke="#EF4444" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                              </svg>

                              </span>

                            </div>
                            <div className="right mt-auto">
                              <h5 className='mt-auto'>
                                {bookings && bookings
                                  .filter(booking =>
                                    booking.package_header.trim().toLowerCase().includes('umrah') &&
                                    booking.departure_flight.departure_location.trim().toLowerCase().includes('madina') &&
                                    moment(booking.departure_flight.departure_flight_date, "YYYY-MM-DD").isSame(date ? date : todayDate, 'day')
                                  )
                                  .reduce((sum, booking) => sum + (booking.totalMembers || 0), 0)}
                              </h5>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4 my-1 px-1">
                        <div className="content pilgrims makkah_bg p-2">
                          <div className="d-flex justify-content-between px-2">
                            <div className="left">
                              <h6>Makkah</h6>
                              <span className='up'>+ 34 <svg width="10" height="11" viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.0835 4.66667L5.00016 1.75M5.00016 1.75L7.91683 4.66667M5.00016 1.75V9.25" stroke="#22C55E" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                              </svg>
                              </span>

                            </div>
                            <div className="right mt-auto">
                              <h5 className='mt-auto'>
                                {bookings && bookings
                                  .filter(booking =>
                                    booking.package_header.trim().toLowerCase().includes('umrah') &&
                                    booking.departure_flight.departure_location.trim().toLowerCase().includes('makkah') &&
                                    moment(booking.departure_flight.departure_flight_date, "YYYY-MM-DD").isSame(date ? date : todayDate, 'day')
                                  )
                                  .reduce((sum, booking) => sum + (booking.totalMembers || 0), 0)}
                              </h5>
                            </div>
                          </div>
                        </div>
                      </div>
                      {bookings && Object.values(bookings
                        .filter(booking =>
                          // Filter out 'madina', 'makkah', 'jeddah' from arrival_location
                          !['madina', 'makkah', 'jeddah'].some(location => booking.departure_flight.departure_location.toLowerCase().includes(location)) &&
                          booking.package_header.trim().toLowerCase().includes('umrah') &&
                          // Check if the arrival flight date matches the selected date (date or todayDate)
                          moment(booking.departure_flight.departure_flight_date, "YYYY-MM-DD").isSame(date ? date : todayDate, 'day')
                        )
                        .reduce((acc, booking) => {
                          const location = booking.departure_flight.departure_location;

                          // If this location has not been added to the accumulator, add it
                          if (!acc[location]) {
                            acc[location] = {
                              location,
                              totalMembers: 0
                            };
                          }

                          // Sum the totalMembers for each location
                          acc[location].totalMembers += booking.totalMembers || 0;

                          return acc;
                        }, {})) // Convert the accumulator object to an array of location stats
                        .filter(locationStat => locationStat.totalMembers > 0) // Only include locations with totalMembers > 0
                        .map(locationStat => (
                          <div key={locationStat.location} className="col-md-4 my-1 px-1">
                            <div className="p-2 content pilgrims airport_bg">
                              <div className="d-flex justify-content-between px-2">
                                <div className="left">
                                  <h6>{locationStat.location}</h6>
                                  <span className='up'>
                                    + {locationStat.totalMembers}{' '}
                                    <svg width="10" height="11" viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M2.0835 4.66667L5.00016 1.75M5.00016 1.75L7.91683 4.66667M5.00016 1.75V9.25" stroke="#22C55E" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </span>
                                </div>
                                <div className="right mt-auto">
                                  <h5 className='mt-auto'>{locationStat.totalMembers}</h5>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}



                    </div>
                  </div>
                </div>
              </div>
            </div> */}
          </div>


          {/* Check IN/OUT Details */}

          {/* Pilgrims Details */}

          <div className="col-md-12 data_table p-2 pb-3 mt-2">
            <div className="d-flex justify-content-between searchbar pb-2 pt-1 px-md-2 my-auto">
              <div className="d-flex left my-auto ps-3 ps-md-0">
                <h4 className='my-auto'>Flights Details</h4>
              </div>
              <div className="d-flex right my-auto">
                {/* <label htmlFor="" className='my-auto'>Filter by</label> */}
                <DatePicker
              // For value, always parse your state 'date' explicitly to dayjs with its known format
              // This ensures dayjs correctly understands the 'YYYY-MM-DD' string from your state.
              value={date ? dayjs(date, "YYYY-MM-DD") : null}
              placeholder="Select Date"
              onChange={handleDateChange} // Use the new handler
              format="DD/MM/YYYY" // Display format for the user
            />
                {/* <input type="date" value={date} onChange={(e) => setDate(e.target.value)} /> */}
              </div>
            </div>
            <hr />

            <div className="row px-1 mt-2">

              <div className="col-lg-12">
                <TableContainer className='dashboard_table content border-0'>
                  <Table stickyHeader aria-label="simple table" className='text-center'>
                    <TableHead>
                      <TableRow>
                        <TableCell className='th text-center py-2 border-0' >Airport City</TableCell>
                        <TableCell className='th text-center py-2 border-0' >Arrivals</TableCell>
                        <TableCell className='th text-center py-2 border-0' >Departures</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>

                      {cityStats && cityStats.map((data, index) => (
                        <TableRow key={data.city}>
                          <TableCell className='td text-center border-0'>{data.city}</TableCell>
                          <TableCell className='td text-center border-0'>
                            <span className='down'><svg width="13" height="13" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M8.25033 6.33333L5.33366 9.25M5.33366 9.25L2.41699 6.33333M5.33366 9.25L5.33366 1.75" stroke="#EF4444" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                            </span>
                            {data.checkin}</TableCell>
                          <TableCell className='td text-center border-0'>
                            <span className='up'><svg width="13" height="13" viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M2.0835 4.66667L5.00016 1.75M5.00016 1.75L7.91683 4.66667M5.00016 1.75V9.25" stroke="#22C55E" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                            </span>
                            {data.checkout}</TableCell>

                        </TableRow>
                      ))}

                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </div>

          </div>

          {/* Hotel Details */}
          <div className="col-md-12 data_table p-2 pb-3 my-2 hotel_details">
            <div className="d-flex justify-content-between searchbar pb-2 pt-1 px-md-2 my-auto">
              <div className="d-flex left my-auto ps-3 ps-md-0">
                <h4 className='my-auto'>Hotel Details</h4>
              </div>
              <div className="right">
                <select name="" id="" value={city} onChange={(e) => setCity(e.target.value)} >
                  <option value="">All</option>
                  <option value="makkah">Makkah</option>
                  <option value="madinah">Madinah</option>

                </select>
              </div>
            </div>
            <hr />
            <div className="row px-3 mt-2">
              {genderStats && genderStats.roomTypeStats
                .filter(state => (state.male + state.female) > 0)
                .map((roomTypeStat) => (
                  <div key={roomTypeStat.roomType} className="col-md-3 my-1 px-1">
                    <div className="content px-3 py-2">
                      <div className="d-flex justify-content-between">
                        <div className="left">
                          <h6>{roomTypeStat.roomType}</h6>
                          <div className="data mt-5">
                            <span className="total_men text-start ms-0">
                              <i className="fa-solid fa-circle me-1"></i>Men: <b>{roomTypeStat.male}</b>
                            </span>
                            <span className="total_women">
                              <br />
                              <i className="fa-solid fa-circle me-1"></i>Women: <b>{roomTypeStat.female}</b>
                            </span>
                          </div>
                        </div>
                        <div className="right">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                dataKey="value"
                                data={[
                                  { name: 'Male', value: roomTypeStat.male },
                                  { name: 'Female', value: roomTypeStat.female },
                                ]}
                                cx="50%"
                                cy="50%"
                                innerRadius={40}
                                outerRadius={60}
                                paddingAngle={2}
                              >
                                {[{ name: 'Male', value: roomTypeStat.male }, { name: 'Female', value: roomTypeStat.female }]
                                  .map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                  ))}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
